import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
  UserManager,
  WebStorageStateStore,
  type INavigator,
  type IWindow,
  type User,
} from "oidc-client-ts";

import { setTokenSource } from "../api/apiClient.js";
import { frontendConfig } from "../api/config.js";

/**
 * Der angemeldete Benutzer, außerhalb von React gehalten.
 *
 * Der API-Client braucht das Zugriffstoken synchron. Käme es aus einem Effekt der
 * Elternkomponente, wäre es zu spät: React führt die Effekte der Kinder zuerst aus, und
 * die erste Abfrage ginge ohne Token hinaus. Deshalb wird der Benutzer hier hinterlegt,
 * bevor React neu zeichnet.
 */
/**
 * Rücksprung-Adresse nach der Anmeldung.
 *
 * Bewusst **nicht** unter /auth: dort liegt in einer Installation hinter einem
 * Reverse Proxy der Identity Provider selbst. Ein Rücksprung auf /auth/callback
 * landete dann bei Keycloak statt in der Oberfläche — und Keycloak antwortet 404.
 */
export const CALLBACK_PATH = "/oidc/callback";
export const NATIVE_CALLBACK_URL = "com.community.project.buma://oidc/callback";

let currentUser: User | null = null;

setTokenSource(() => (currentUser && !currentUser.expired ? currentUser.access_token : null));

export function rememberUser(user: User | null): void {
  currentUser = user;
}

let pendingRefresh: Promise<User | null> | null = null;

/**
 * Stellt eine gespeicherte Sitzung wieder her und erneuert abgelaufene Access-Tokens.
 * Netzwerkfehler löschen den Refresh-Token nicht; beim nächsten Resume wird erneut
 * versucht. Nur eine endgültig abgelehnte Sitzung wird aus dem Speicher entfernt.
 */
export function restoreUser(): Promise<User | null> {
  pendingRefresh ??= (async () => {
    const stored = await userManager().getUser();
    if (!stored || !stored.expired) return stored;
    if (!stored.refresh_token) return null;
    try {
      return await userManager().signinSilent();
    } catch (error) {
      if (isTerminalRefreshError(error)) await userManager().removeUser();
      return null;
    }
  })().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
}

export async function registerAccount(): Promise<void> {
  await userManager().signinRedirect({ prompt: "create" });
}

export type PasswordSigninResult = "signed-in" | "invalid-credentials" | "redirected";

/**
 * Anmeldung mit E-Mail und Passwort direkt im buma-Formular, ohne Umweg über die
 * Keycloak-Seite (Password-Grant am Token-Endpunkt).
 *
 * Kann Keycloak die Anmeldung so nicht abschließen — der Client erlaubt den Grant
 * nicht, oder das Konto hat noch offene Schritte wie ein Initialpasswort oder eine
 * E-Mail-Bestätigung —, geht es mit vorausgefüllter Adresse auf die Keycloak-Seite.
 * Dort lassen sich diese Schritte erledigen; hier nicht.
 */
export async function signinWithPassword(email: string, password: string): Promise<PasswordSigninResult> {
  try {
    await userManager().signinResourceOwnerCredentials({ username: email, password });
    return "signed-in";
  } catch (error) {
    const { code, description } = oidcError(error);
    if (code === "invalid_grant" && !/not fully set up/i.test(description)) {
      return "invalid-credentials";
    }
    if (code === "invalid_grant" || code === "unauthorized_client" || code === "unsupported_grant_type") {
      await userManager().signinRedirect({ extraQueryParams: { login_hint: email } });
      return "redirected";
    }
    throw error;
  }
}

/** Springt über `kc_idp_hint` direkt zum angegebenen Identity Provider, etwa Microsoft. */
export async function signinWithIdentityProvider(alias: string): Promise<void> {
  await userManager().signinRedirect({ extraQueryParams: { kc_idp_hint: alias } });
}

/** Öffnet „Passwort vergessen" von Keycloak; der Link in der Mail führt zurück zu buma. */
export async function openPasswordReset(): Promise<void> {
  const { oidcIssuer, oidcClientId } = frontendConfig();
  const url = `${oidcIssuer}/login-actions/reset-credentials?client_id=${encodeURIComponent(oidcClientId)}`;
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
  } else {
    window.location.assign(url);
  }
}

function oidcError(error: unknown): { code: string; description: string } {
  if (!error || typeof error !== "object" || !("error" in error)) return { code: "", description: "" };
  const description = "error_description" in error ? String(error.error_description ?? "") : "";
  return { code: String(error.error ?? ""), description };
}

/**
 * Anmeldung über Keycloak mit Authorization Code und PKCE. Es gibt kein Client-Secret;
 * die Oberfläche ist ein öffentlicher Client (Issue #28).
 */
let manager: UserManager | null = null;

const nativeBrowserNavigator: INavigator = {
  async prepare(): Promise<IWindow> {
    return {
      async navigate({ url }) {
        await Browser.open({ url });
        return { url };
      },
      close() {
        void Browser.close();
      },
    };
  },
  async callback(): Promise<void> {},
};

export function userManager(): UserManager {
  if (!manager) {
    const config = frontendConfig();
    const origin = window.location.origin;
    const redirectUri = Capacitor.isNativePlatform()
      ? NATIVE_CALLBACK_URL
      : `${origin}${CALLBACK_PATH}`;
    manager = new UserManager(
      {
        authority: config.oidcIssuer,
        client_id: config.oidcClientId,
        redirect_uri: redirectUri,
        post_logout_redirect_uri: origin,
        response_type: "code",
        scope: Capacitor.isNativePlatform()
          ? "openid profile email offline_access"
          : "openid profile email",
        // Die Sitzung wird im stillen Hintergrund erneuert, solange der Tab offen ist.
        automaticSilentRenew: true,
        // Beim bewussten Abmelden auch den langlebigen Mobile-Refresh-Token widerrufen.
        revokeTokensOnSignout: true,
        revokeTokenTypes: ["refresh_token"],
        userStore: new WebStorageStateStore({ store: window.localStorage }),
      },
      Capacitor.isNativePlatform() ? nativeBrowserNavigator : undefined,
    );
  }
  return manager;
}

function isTerminalRefreshError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("error" in error)) return false;
  return ["invalid_grant", "login_required", "interaction_required"].includes(String(error.error));
}


/**
 * Verarbeitet den Rücksprung von Keycloak — und zwar **genau einmal**.
 *
 * Der Autorisierungscode ist ein Einmal-Ticket: der zweite Einlöseversuch scheitert mit
 * 400. React startet Effekte im StrictMode aber absichtlich doppelt, und ohne diese
 * Absicherung machte der zweite Lauf die Anmeldung des ersten wieder zunichte. Beide
 * Läufe warten deshalb auf dasselbe Versprechen.
 */
let pendingCallback: Promise<User | null> | null = null;

export function isCallbackUrl(url = window.location.href): boolean {
  if (url.startsWith(NATIVE_CALLBACK_URL)) {
    return true;
  }
  return new URL(url).pathname === CALLBACK_PATH;
}

export function completeSignin(url?: string): Promise<User | null> {
  pendingCallback ??= (async () => {
    try {
      const user = await userManager().signinCallback(url);
      return user ?? (await userManager().getUser());
    } finally {
      if (Capacitor.isNativePlatform()) {
        void Browser.close();
      }
      // Code und State aus der Adresszeile entfernen, damit ein Neuladen nicht scheitert.
      window.history.replaceState({}, "", "/");
    }
  })();
  return pendingCallback;
}
